import React, { useEffect, useMemo, useState } from 'react';
import {
  Btn, DangerBtn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtDate, panelPost, useAdminGet,
} from './kit';
import { FloatingToast } from '../InlineAlert';
import { SEARCH_ENDPOINT } from '../../services/apiService';
import SearchIndexesPanel from './SearchIndexesPanel';
import type { AdminTargetIn } from './adminNav';

/**
 * System group — admin_panel_v2's ops tooling: platform totals and active user
 * sessions (Tab 6), landing-page rate limits with per-IP / global reset (Tab 6),
 * the Postgres table listing and SQL console (Tab 11, now hard-limited to
 * read-only SELECT/WITH in a read-only transaction, 500 rows), the SQLite
 * technology catalog's own inventory and console (Tab 21), the background batch
 * queue the AI labs run on, the search indexes after a corpus load (status
 * per layer and per university, and a one-click catch-up — SearchIndexesPanel),
 * and a read-only view of which server-side integrations are configured.
 *
 * The Streamlit sidebar's API-keys form is deliberately NOT migrated: keys live
 * in the server environment only. (The legacy panel shipped live secrets as
 * input defaults — that pattern dies with it.) The Integrations tab is its
 * replacement: booleans, never values.
 */

/**
 * How many documents a search holds and can page through.
 *
 * Raising this costs nothing extra to retrieve: both engines already return
 * their candidates and the pipeline already fuses and ranks them — the cap
 * only decides how many survive. Measured over 1,835 real queries, the fused
 * set has a median of 144 and a p90 of 193, so a cap of 100 discards a median
 * of 44 documents per search AFTER the work is done.
 */
const SearchCap: React.FC = () => {
  const caps = useAdminGet('system', 'search_caps');
  const [val, setVal] = useState('');
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const d: any = caps.data;
  const current = d?.max_results;

  return (
    <Section title="Search result cap"
      right={<Btn small onClick={caps.reload}>Refresh</Btn>}>
      {caps.loading ? <Loading /> : (
        <div className="flex flex-col gap-2">
          <div className="text-xs text-grayscale-1100">
            Currently <b>{current}</b> documents ({d?.max_pages} pages of {d?.hits_per_page}).
            Both engines are asked for this many candidates each, so the fused set
            cannot exceed twice it — measured p90 of that set is 193, which is why
            200 is the point past which raising this stops helping without also
            costing retrieval time.
          </div>
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
          <div className="flex gap-2 items-center flex-wrap">
            <TextInput value={val} onChange={setVal}
              placeholder={`new cap (e.g. 200) — now ${current ?? '?'}`} />
            <Btn onClick={async () => {
              const n = Number(val);
              if (!n || n < 10) { setMsg({ kind: 'error', text: 'Enter a number of at least 10.' }); return; }
              const { data, error } = await panelPost('system', 'set_search_cap', { max_results: n });
              if (error) setMsg({ kind: 'error', text: error });
              else {
                // Rounded up to a whole page, so say what was actually stored
                // rather than echoing back what was typed.
                setMsg({ kind: 'ok', text: `Cap is now ${data.max_results} documents (${data.max_pages} pages).` });
                setVal(''); caps.reload();
              }
            }}>Save</Btn>
            <span className="text-[11px] text-grayscale-1100">
              Rounded up to a whole page. Takes effect on the next search.
            </span>
          </div>
        </div>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Search engine (v2) — which engine the website's main search runs on, and
// its knobs. Backed by app_settings key "search_v2" through the dispatcher
// pair system/search_v2 (GET) and system/set_search_v2 (POST). The server
// caches the setting for ~15 s, so a save reaches live traffic within that
// window with no deploy and no restart. "Legacy /search" is the full escape
// hatch back to the pre-v2 hybrid path.
//
// Simple queries default to Typesense: the orchestrator starts that retrieval
// speculatively at t0 alongside classify / filter detection / expansion, so a
// keyword query costs one ~100 ms Typesense call (re-run once if detected
// filters changed anything) instead of the ~9 s native SQL scan. The fast
// path skips the LLM classifier entirely for one- or two-word queries with no
// qualifier or filter words — the classifier still runs for everything else.
// Two further knobs trim the quick route's LLM wait: filter_detect_skip drops
// the AI filter-extractor call when the query holds no filter vocabulary
// (country / university / category / TRL words), and expand_blocking_simple
// decides whether the typesense / sql routes wait for query expansion at all
// (off: expansion keeps running in the background and is cached for a later
// deep search). Both keys are newer than some deployed servers, so the card
// only sends them when the server advertised them or the admin changed them.
// ---------------------------------------------------------------------------
interface V2Settings {
  engine: string;
  top_n: number;
  /** How many a keyword search keeps. The browser holds the whole set and
   *  filters it locally, so this is also the ceiling a facet can filter
   *  without a new search. Capped at 250 — Typesense's own per_page limit. */
  top_n_simple: number;
  /** How many a deep search keeps. Each one costs an LLM rescore. */
  top_n_complex: number;
  /** 0–100. Hides deep-search results the AI scored below this. The quick
   *  routes have no relevance score, only rank position, so this deliberately
   *  does not apply to them. 0 disables. */
  min_relevance_deep: number;
  retrieve_n: number;
  rescore_chunk: number;
  rescore_concurrency: number;
  expand: boolean;
  classify: boolean;
  classify_fast_path: boolean;
  simple_route: string;
  /**
   * After a search that took a simple route (typesense / sql), the website
   * offers "Run deep search" — the same query re-run through the engine with
   * force_route 'engine'. Off: the offer never appears (forced runs still work).
   */
  deep_search_prompt: boolean;
  /**
   * Debug mode. Shows route, engine, classification source, cache state and
   * total time beside the result count — and ONLY to admin accounts, which is
   * enforced separately on the page. Off means even an admin sees nothing, so
   * a demo is clean.
   */
  show_route_badge: boolean;
  /**
   * Skip the AI filter-extractor call when the query has no filter words
   * (no country / university / category / TRL vocabulary). The speculative
   * Typesense result is then final for simple queries. Default on.
   */
  filter_detect_skip: boolean;
  /**
   * Make the quick (typesense / sql) route wait for query expansion before
   * answering. Off (default): the route answers as soon as classify, filters
   * and Typesense are done; expansion finishes in the background and is
   * cached for a later deep search.
   */
  expand_blocking_simple: boolean;
  /**
   * Quick (typesense / sql) searches: score every held result by keyword
   * match, sort by it, and show that score as the Relevance %. Off: the
   * engine's own order, with the browser working the pill out per page.
   */
  keyword_rank_simple: boolean;
  /**
   * Accounts that cannot filter (Explorer) do not get the filters found in
   * the query text either ("IIT Bombay TRL5"): the remaining words are
   * searched unfiltered and the page offers the upgrade. Off: detected
   * filters apply to everyone, as before. Default on.
   */
  lock_detected_filters_free: boolean;
}
/** Numeric knobs are edited as text so a field can be emptied mid-typing. */
type V2Form = Omit<V2Settings, 'top_n' | 'retrieve_n' | 'rescore_chunk' | 'rescore_concurrency'> & {
  top_n: string; retrieve_n: string; rescore_chunk: string; rescore_concurrency: string;
};
type V2NumKey = 'top_n' | 'retrieve_n' | 'rescore_chunk' | 'rescore_concurrency'
  | 'top_n_simple' | 'top_n_complex' | 'min_relevance_deep';

const V2_DEFAULTS: V2Settings = {
  engine: 'rag_pinecone', top_n: 25, retrieve_n: 40, rescore_chunk: 8,
  rescore_concurrency: 5, expand: true, classify: true, classify_fast_path: true,
  simple_route: 'typesense', deep_search_prompt: true, show_route_badge: false,
  top_n_simple: 500, top_n_complex: 25, min_relevance_deep: 40,
  filter_detect_skip: true, expand_blocking_simple: false, keyword_rank_simple: true,
  lock_detected_filters_free: true,
};
const V2_ENGINE_LABELS: Record<string, string> = {
  rag_pinecone: 'RAG · Pinecone + LLM rescore (default)',
  rag_ensemble: 'RAG · ensemble pool (BM25 + FAISS large) + LLM rescore',
  rag_bge_small: 'RAG · bge-small numpy + LLM rescore',
  algolia: 'Algolia (legacy index)',
  hybrid: 'Hybrid (Typesense + Pinecone)',
  pinecone: 'Pinecone only',
  typesense: 'Typesense only',
  legacy: 'Legacy /search (old hybrid path)',
};
// Order here is the fallback order of the select when the API sends no
// options.simple_routes; the API's own order wins when it does.
const V2_ROUTE_LABELS: Record<string, string> = {
  typesense: 'Typesense (fast keyword, native filters) — default',
  sql: 'SQL keyword search (legacy native)',
  engine: 'Selected engine',
};
const V2_NUMERIC: { key: V2NumKey; label: string; hint: string }[] = [
  { key: 'top_n', label: 'Results (top_n)', hint: 'Final results a search returns.' },
  { key: 'retrieve_n', label: 'Candidates (retrieve_n)', hint: 'Fetched per query variant before fusion and rescore.' },
  { key: 'rescore_chunk', label: 'Rescore chunk', hint: 'Candidates per LLM relevance call.' },
  { key: 'rescore_concurrency', label: 'Rescore concurrency', hint: 'Parallel LLM relevance calls per search.' },
  { key: 'top_n_simple', label: 'Results kept — simple',
    hint: 'Keyword searches. The set is held server-side and every page and facet is served off it without searching again, so this is also how deep a filter can reach. Above 250 \u2014 Typesense\u2019s per-page ceiling \u2014 it is fetched as extra pages. Max 500.' },
  { key: 'top_n_complex', label: 'Results kept — deep',
    hint: 'Deep searches pay an LLM rescore per result, so this stays small.' },
  { key: 'min_relevance_deep', label: 'Hide deep results scored below',
    hint: '0\u2013100, on the AI\u2019s own relevance judgement. Deep search only: the quick routes have no relevance score to threshold, just rank position. 0 disables.' },
];

type V2BoolKey = 'expand' | 'classify' | 'classify_fast_path' | 'deep_search_prompt'
  | 'filter_detect_skip' | 'expand_blocking_simple' | 'show_route_badge' | 'keyword_rank_simple'
  | 'lock_detected_filters_free';
const V2_BOOLS: V2BoolKey[] = ['expand', 'classify', 'classify_fast_path', 'deep_search_prompt',
  'filter_detect_skip', 'expand_blocking_simple', 'show_route_badge', 'keyword_rank_simple',
  'lock_detected_filters_free'];
/**
 * Keys an older server may not know. set_search_v2 rejects the whole save on
 * an unknown key, so these ride along only when the GET advertised them or
 * the admin actually changed them (then the server's verbatim "unknown
 * setting" message says exactly why the save failed).
 */
const V2_OPTIONAL: V2BoolKey[] = ['filter_detect_skip', 'expand_blocking_simple',
  'show_route_badge', 'keyword_rank_simple', 'lock_detected_filters_free'];

/** Whether the GET exposed a settings key — flat, or under settings/defaults/labels. */
const v2Advertised = (d: any, key: string): boolean => {
  if (!d || typeof d !== 'object') return false;
  if (key in d) return true;
  return ['settings', 'value', 'current', 'config', 'defaults', 'labels']
    .some(k => d[k] && typeof d[k] === 'object' && key in d[k]);
};

const v2ToForm = (s: V2Settings): V2Form => ({
  ...s, top_n: String(s.top_n), retrieve_n: String(s.retrieve_n),
  rescore_chunk: String(s.rescore_chunk), rescore_concurrency: String(s.rescore_concurrency),
});

/** The GET may hand back the settings flat or nested (settings/value/current). */
const pickV2Settings = (d: any): Partial<V2Settings> => {
  if (!d || typeof d !== 'object') return {};
  const nested = ['settings', 'value', 'current', 'config']
    .map(k => d[k]).find(v => v && typeof v === 'object' && 'engine' in v);
  const src: any = nested ?? d;
  const out: Partial<V2Settings> = {};
  if (typeof src.engine === 'string') out.engine = src.engine;
  if (typeof src.simple_route === 'string') out.simple_route = src.simple_route;
  for (const k of ['top_n', 'retrieve_n', 'rescore_chunk', 'rescore_concurrency',
                   'top_n_simple', 'top_n_complex', 'min_relevance_deep'] as V2NumKey[]) {
    const n = Number(src[k]);
    if (src[k] !== null && src[k] !== undefined && src[k] !== '' && Number.isFinite(n)) out[k] = n;
  }
  for (const k of V2_BOOLS) {
    if (typeof src[k] === 'boolean') out[k] = src[k];
    else if (src[k] === 1 || src[k] === 0) out[k] = src[k] === 1;
  }
  return out;
};

type V2Opt = { value: string; label: string };
/**
 * Choices for a select. The API's list wins when it sends one (as strings,
 * {value,label} objects, or a value→label map); otherwise the contract's list.
 * Known values keep the product wording; unknown ones show as sent. The value
 * currently stored is always selectable, flagged if the API no longer offers it.
 */
const pickV2Options = (d: any, field: 'engine' | 'simple_route', fallback: string[],
                       labels: Record<string, string>, current: string): V2Opt[] => {
  const raw = d?.options?.[field] ?? d?.options?.[`${field}s`] ?? d?.[`${field}_options`]
    ?? d?.[`${field}s`] ?? d?.allowed?.[field] ?? d?.choices?.[field];
  let list: any[];
  if (Array.isArray(raw) && raw.length) list = raw;
  else if (raw && typeof raw === 'object') list = Object.entries(raw).map(([value, label]) => ({ value, label }));
  else list = fallback;
  const opts: V2Opt[] = [];
  for (const item of list) {
    const value = typeof item === 'string' ? item : String(item?.value ?? item?.key ?? item?.id ?? '');
    if (!value || opts.some(o => o.value === value)) continue;
    const apiLabel = item && typeof item === 'object' ? (item.label ?? item.name) : undefined;
    opts.push({ value, label: labels[value] ?? (typeof apiLabel === 'string' ? apiLabel : value) });
  }
  if (current && !opts.some(o => o.value === current)) {
    opts.push({ value: current, label: `${labels[current] ?? current} (not offered by the API)` });
  }
  return opts;
};

const pickV2Saved = (d: any): { by?: string; at?: string } => {
  const src = d?.last_saved ?? d?.saved ?? d?.meta ?? d ?? {};
  const by = src.updated_by ?? src.saved_by ?? src.last_saved_by ?? src.by;
  const at = src.updated_at ?? src.saved_at ?? src.last_saved_at ?? src.at;
  return { by: by ? String(by) : undefined, at: at ? String(at) : undefined };
};

const Check: React.FC<{ label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }> =
  ({ label, hint, checked, onChange }) => (
  <label className="flex items-start gap-2 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      className="mt-1 accent-primary-500" />
    <span className="flex flex-col">
      <span className="text-sm text-grayscale-1300">{label}</span>
      {hint && <span className="text-[11px] text-grayscale-1100">{hint}</span>}
    </span>
  </label>
);

const SearchEngineV2: React.FC = () => {
  const cfg = useAdminGet('system', 'search_v2');
  const [base, setBase] = useState<V2Settings>(V2_DEFAULTS);
  const [form, setForm] = useState<V2Form>(v2ToForm(V2_DEFAULTS));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const [toast, setToast] = useState<{ variant: 'success' | 'error'; text: string } | null>(null);

  // Re-baseline on every server answer (mount, Refresh, after a save). A
  // failed GET leaves the contract defaults in the form so the card is still
  // usable — the POST will say if the server disagrees.
  useEffect(() => {
    if (cfg.loading) return;
    const next = { ...V2_DEFAULTS, ...pickV2Settings(cfg.data) };
    setBase(next); setForm(v2ToForm(next));
  }, [cfg.data, cfg.loading]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const engines = useMemo(
    () => pickV2Options(cfg.data, 'engine', Object.keys(V2_ENGINE_LABELS), V2_ENGINE_LABELS, form.engine),
    [cfg.data, form.engine]);
  const routes = useMemo(
    () => pickV2Options(cfg.data, 'simple_route', Object.keys(V2_ROUTE_LABELS), V2_ROUTE_LABELS, form.simple_route),
    [cfg.data, form.simple_route]);
  const saved = pickV2Saved(cfg.data);
  const baseForm = v2ToForm(base);
  const dirty = (Object.keys(baseForm) as (keyof V2Form)[]).some(k => form[k] !== baseForm[k]);
  const set = <K extends keyof V2Form>(k: K, v: V2Form[K]) => setForm(f => ({ ...f, [k]: v }));
  const toLabels = (opts: V2Opt[]) => Object.fromEntries(opts.map(o => [o.value, o.label]));

  const save = async () => {
    const nums: Partial<Record<V2NumKey, number>> = {};
    const bad: string[] = [];
    for (const f of V2_NUMERIC) {
      const n = Number(form[f.key]);
      // The relevance threshold's floor is 0, which is how it is switched off;
      // every other knob is a count and must be at least 1.
      const min = f.key === 'min_relevance_deep' ? 0 : 1;
      if (String(form[f.key]).trim() === '' || !Number.isInteger(n) || n < min) bad.push(f.label);
      else nums[f.key] = n;
    }
    if (bad.length) {
      setMsg({ kind: 'error', text: `Whole numbers, please (0 is allowed only for the relevance threshold): ${bad.join(', ')}.` });
      return;
    }
    const body: Partial<V2Settings> = {
      engine: form.engine, simple_route: form.simple_route,
      expand: form.expand, classify: form.classify,
      classify_fast_path: form.classify_fast_path,
      deep_search_prompt: form.deep_search_prompt, ...nums,
    };
    for (const k of V2_OPTIONAL) {
      if (v2Advertised(cfg.data, k) || form[k] !== base[k]) body[k] = form[k];
    }
    setSaving(true); setMsg(null);
    const { data, error } = await panelPost('system', 'set_search_v2', body);
    setSaving(false);
    if (error) {
      // Validation failures (unknown engine, out-of-range knob) come back as
      // the server's own message — show it verbatim so the fix is obvious.
      setMsg({ kind: 'error', text: error });
      setToast({ variant: 'error', text: error });
      return;
    }
    // Say what was actually stored rather than echoing the form: the server
    // may clamp or normalise, and may or may not echo the settings back.
    const stored: V2Settings = { ...base, ...body, ...pickV2Settings(data) };
    const label = V2_ENGINE_LABELS[stored.engine] ?? stored.engine;
    const route = V2_ROUTE_LABELS[stored.simple_route] ?? stored.simple_route;
    setMsg({ kind: 'ok', text: `Saved. Engine: ${label}. Simple queries: ${route}. Live on the site within about 15 seconds.` });
    setToast({ variant: 'success', text: `Search engine saved: ${label}` });
    cfg.reload();
  };

  return (
    <Section title="Search engine (v2)"
      right={<Btn small onClick={cfg.reload}>Refresh</Btn>}>
      <p className="text-xs text-grayscale-1100 -mt-1">
        What <span className="font-mono">{SEARCH_ENDPOINT}</span> runs for the website's main
        search. Each query is classified simple / complex while filters are detected, the
        query is expanded and a Typesense retrieval is started speculatively, all in parallel;
        simple queries take the simple route below, complex ones go to the engine. Stored
        in <span className="font-mono">app_settings</span> and picked up by the server within
        about 15 seconds — no deploy, no restart.
      </p>
      {cfg.error && <Msg kind="error">Could not load the current setting: {cfg.error}</Msg>}
      {cfg.loading ? <Loading /> : (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 items-start">
            <span className="text-xs font-medium text-grayscale-1200">Engine</span>
            <Select value={form.engine} onChange={v => set('engine', v)}
              options={engines.map(o => o.value)} labels={toLabels(engines)} />
          </label>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {V2_NUMERIC.map(f => (
              <label key={f.key} className="flex flex-col gap-1">
                <span className="text-xs font-medium text-grayscale-1200">{f.label}</span>
                <input type="number" min={1} step={1} value={form[f.key]}
                  onChange={e => set(f.key, e.target.value)}
                  className="h-9 px-3 rounded-md border-[0.5px] border-primary-300 bg-white text-sm" />
                <span className="text-[11px] text-grayscale-1100">{f.hint}</span>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 items-start">
            <Check label="Query expansion" checked={form.expand} onChange={v => set('expand', v)}
              hint="Expanded query is fused with the raw one, never replaces it." />
            <Check label="Classify simple / complex" checked={form.classify} onChange={v => set('classify', v)}
              hint="LLM classification with a heuristic fallback; decides which route a query takes." />
            <Check label="Fast path for short queries" checked={form.classify_fast_path}
              onChange={v => set('classify_fast_path', v)}
              hint="One- or two-word queries with no qualifier or filter words are classified simple without the LLM call; everything else still goes through the classifier." />
            <Check label="Offer 'Run deep search' after simple searches" checked={form.deep_search_prompt}
              onChange={v => set('deep_search_prompt', v)}
              hint="After a keyword-route result the site shows a one-line offer to re-run the same query through the engine (LLM rescore, ~10-15 s). Users can dismiss it per query." />
            <Check label="Skip AI filter detection when the query has no filter words"
              checked={form.filter_detect_skip} onChange={v => set('filter_detect_skip', v)}
              hint="The LLM filter extractor (~2 s) runs only when the query carries country, university, category or TRL vocabulary; otherwise the speculative Typesense result is final. Filters chosen in the UI are always applied." />
            <Check label="Quick route waits for query expansion"
              checked={form.expand_blocking_simple} onChange={v => set('expand_blocking_simple', v)}
              hint="Off: Typesense / SQL results return as soon as classify, filters and retrieval are done, while the ~2-3 s expansion finishes in the background and is cached for a later deep search. On: the route waits for expansion first, as before." />
            <Check label="Quick search: sort by keyword match (the Relevance % shown)"
              checked={form.keyword_rank_simple} onChange={v => set('keyword_rank_simple', v)}
              hint="Every quick-search result is scored by where the query's words appear (title, keywords, summary, categories) and the whole set is sorted by that score before paging, so page 1 holds the best matches and the Relevance % is the number the order was built from. Off: Typesense's own order, which favours the exact phrase in any field." />
            <Check label="Lock detected filters for Explorers"
              checked={form.lock_detected_filters_free} onChange={v => set('lock_detected_filters_free', v)}
              hint="Accounts that cannot filter do not get the filters found in their query either: 'IIT Bombay TRL5' searches without the university or TRL filter, and the page shows 'Auto-detected filter — upgrade to apply' and opens the upgrade sheet once. Off: detected filters apply to everyone, as before. Paid and trial accounts are not affected." />
            <label className="flex flex-col gap-1 items-start">
              <span className="text-xs font-medium text-grayscale-1200">Simple queries go to</span>
              <Select value={form.simple_route} onChange={v => set('simple_route', v)}
                options={routes.map(o => o.value)} labels={toLabels(routes)} />
              <span className="text-[11px] text-grayscale-1100">
                Typesense runs the raw query with the merged filters natively (~100 ms). SQL is the
                pre-Typesense keyword scan. "Selected engine" sends simple queries through the
                engine above, LLM rescore included.
              </span>
            </label>
          </div>
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
          <div className="flex items-center gap-3 flex-wrap">
            <Btn kind="primary" disabled={!dirty || saving} onClick={save}
              title={dirty ? undefined : 'Nothing changed yet'}>
              {saving ? 'Saving…' : 'Save'}
            </Btn>
            {dirty && !saving && (
              <Btn small onClick={() => { setForm(v2ToForm(base)); setMsg(null); }}>Discard changes</Btn>
            )}
            <span className="text-[11px] text-grayscale-1100">
              {saved.by || saved.at
                ? `Last saved${saved.by ? ` by ${saved.by}` : ''}${saved.at ? ` · ${fmtDate(saved.at)}` : ''}`
                : 'No save recorded yet — defaults in effect.'}
            </span>
          </div>
        </div>
      )}
      <FloatingToast variant={toast?.variant} message={toast?.text} visible={!!toast} />
    </Section>
  );
};

const TABS = ['Sessions', 'Landing limits', 'Database', 'SQL console',
  'Catalog DB', 'Jobs', 'Search indexes', 'Integrations'];

const SystemPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const [tab, setTab] = useState(TABS[0]);
  // Deep link: /admin?group=system&tab=integrations (any tab, by its name in
  // lower case with _ or - for spaces) — Horizon data's "no key" note links here.
  useEffect(() => {
    const want = String(target?.tab || '').toLowerCase().replace(/[_-]+/g, ' ').trim();
    const hit = want && TABS.find(t => t.toLowerCase() === want);
    if (hit) setTab(hit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Sessions' && <Sessions />}
      {tab === 'Landing limits' && <LandingLimits />}
      {tab === 'Database' && <Database />}
      {tab === 'SQL console' && (
        <SqlRunner action="sql" title="SQL console — Postgres, read-only"
          blurb="One SELECT/WITH statement, run in a read-only transaction, capped at 500 rows. Writes are rejected by Postgres itself, not just by the input check."
          initial="SELECT COUNT(*) AS users FROM users" csvName="query_result" />
      )}
      {tab === 'Catalog DB' && <CatalogDb />}
      {tab === 'Jobs' && <Jobs />}
      {tab === 'Search indexes' && <SearchIndexesPanel />}
      {tab === 'Integrations' && <Integrations />}
    </div>
  );
};

// ---------------------------------------------------------------------------
const StatusPill: React.FC<{ value: string }> = ({ value }) => {
  const v = (value || '').toLowerCase();
  const look = v === 'done' || v === 'completed'
    ? 'text-green-700 bg-green-50 border-green-200'
    : v === 'failed' || v === 'error'
      ? 'text-red-600 bg-red-50 border-red-200'
      : v === 'running'
        ? 'text-amber-700 bg-amber-50 border-amber-200'
        : 'text-grayscale-1200 bg-grayscale-200 border-grayscale-300';
  return <span className={`text-xs rounded-full px-2 py-0.5 border ${look}`}>{value || '—'}</span>;
};

// ---------------------------------------------------------------------------
const Sessions: React.FC = () => {
  const sessions = useAdminGet('system', 'active_sessions');
  const stats = useAdminGet('system', 'platform_stats');
  const rows: any[] = sessions.data?.rows || [];
  // The handler counts with COUNT(*) and only caps the *table* at 100 rows, so
  // the KPIs must read its totals — rows.length is the page size, not the fleet.
  const total: number = sessions.data?.total ?? rows.length;
  const uniqueUsers: number = sessions.data?.unique_users ?? 0;
  const truncated: boolean = !!sessions.data?.truncated;
  const avg = rows.length ? (rows.reduce((a, r) => a + (r.hours || 0), 0) / rows.length).toFixed(1) : '0';
  const n = (v: any) => (typeof v === 'number' ? v.toLocaleString() : v ?? 0);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Platform statistics"
        right={<Btn small onClick={stats.reload}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Raw platform totals. These are deliberately NOT filtered by the analytics exclusion
          list, so they read higher than the User Analytics screens — that gap is the internal
          accounts, not a discrepancy.
        </p>
        {stats.error && <Msg kind="error">{stats.error}</Msg>}
        {stats.loading ? <Loading /> : (
          <KPIRow>
            <KPI label="Total users" value={n(stats.data?.total_users)} />
            <KPI label="Total searches" value={n(stats.data?.total_searches)} />
            <KPI label="Searches, 7 days" value={n(stats.data?.searches_7d)}
              hint={`${n(stats.data?.searchers_7d)} distinct accounts`} />
            <KPI label="Total chats" value={n(stats.data?.total_chats)} hint="AI chat, the older feature" />
            <KPI label="Total messages" value={n(stats.data?.total_messages)} />
            <KPI label="Active chats" value={n(stats.data?.active_chats_7d)} hint="updated in the last 7 days" />
          </KPIRow>
        )}
      </Section>
      <KPIRow>
        <KPI label="Active sessions" value={n(total)} />
        <KPI label="Unique users" value={n(uniqueUsers)} />
        <KPI label="Avg duration" value={`${avg} h`}
          hint={truncated ? `across the ${rows.length} shown` : undefined} />
      </KPIRow>
      <SearchCap />
      <SearchEngineV2 />

      <Section title="Active user sessions" right={<Btn small onClick={sessions.reload}>Refresh</Btn>}>
        {sessions.error && <Msg kind="error">{sessions.error}</Msg>}
        {truncated && (
          <p className="text-xs text-amber-700 -mt-1">
            Showing the {rows.length} most recently started of {n(total)} active sessions.
            The KPIs above count all of them; the table, the filter and the CSV cover only
            these {rows.length}.
          </p>
        )}
        {sessions.loading ? <Loading /> : (
          <DataTable rows={rows} searchable csvName="active_sessions"
            columns={['user', 'email', 'ip', 'hours', 'created']}
            labels={{ hours: 'Duration (h)' }}
            empty="No live sessions right now." />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
const LandingLimits: React.FC = () => {
  const [ip, setIp] = useState('');
  const limits = useAdminGet('system', 'landing_limits', { ip }, [ip]);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  // The IP filter is applied in SQL, so `total` is the number of MATCHING IPs
  // and `rows` is only the newest 100 of them.
  const rows: any[] = limits.data?.rows || [];
  const total: number = limits.data?.total ?? rows.length;
  const truncated: boolean = !!limits.data?.truncated;
  const n = (v: number) => v.toLocaleString();

  const reset = async (targetIp: string | null) => {
    const { data, error } = await panelPost('system', 'reset_landing_limit',
      targetIp ? { ip: targetIp } : {});
    if (error || data?.success === false) setMsg({ kind: 'error', text: error || data?.error || 'Reset failed' });
    else { setMsg({ kind: 'ok', text: `Reset ${data.reset} IP(s).` }); limits.reload(); }
  };

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label={ip.trim() ? 'IPs matching' : 'IPs tracked'} value={n(total)} />
        <KPI label="Sessions" value={n(rows.reduce((a, r) => a + (r.session_count || 0), 0))}
          hint={truncated ? 'in the rows shown' : undefined} />
        <KPI label="Messages" value={n(rows.reduce((a, r) => a + (r.message_count || 0), 0))}
          hint={truncated ? 'in the rows shown' : undefined} />
      </KPIRow>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {limits.error && <Msg kind="error">{limits.error}</Msg>}
      <Section title="Landing-page rate limits (anonymous visitors)"
        right={<div className="flex gap-2 items-center">
          <TextInput value={ip} onChange={setIp} placeholder="Filter by IP…" />
          <DangerBtn small onConfirm={() => reset(null)}>Reset ALL IPs</DangerBtn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The IP filter is matched by the server across every tracked IP, so any of
          them can be found and reset from here. “Reset ALL IPs” always clears the
          whole table — it ignores the filter.
        </p>
        {truncated && (
          <p className="text-xs text-amber-700">
            Showing the {rows.length} most recently active of {n(total)}
            {ip.trim() ? ' matching' : ' tracked'} IPs — type more of an IP above to
            narrow it down to the one you want to reset.
          </p>
        )}
        {limits.loading ? <Loading /> : (
          <DataTable rows={rows} csvName="landing_limits"
            empty={ip.trim() ? `No tracked IP matches “${ip.trim()}”.` : 'No rate-limited IPs.'}
            actions={r => <Btn small onClick={() => reset(r.ip_address)}>Reset</Btn>} />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
const Database: React.FC = () => {
  const tables = useAdminGet('system', 'tables');
  return (
    <Section title="Postgres tables (live row estimates)"
      right={<Btn small onClick={tables.reload}>Refresh</Btn>}>
      {tables.error && <Msg kind="error">{tables.error}</Msg>}
      {tables.loading ? <Loading /> : (
        <DataTable rows={tables.data || []} searchable csvName="db_tables" maxHeight="36rem"
          empty="No tables reported." />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Catalog DB — the SQLite technology catalog, a different database from the
// Postgres one above (same legacy line numbers, different file).
// ---------------------------------------------------------------------------
const CatalogDb: React.FC = () => {
  const tables = useAdminGet('system', 'sqlite_tables');
  const templates = useAdminGet('system', 'sqlite_templates');
  const rows: any[] = tables.data?.rows || [];
  const unreadable = rows.filter(r => r.rows === 'Error').length;

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Tables & views" value={tables.data?.tables ?? 0} />
        <KPI label="Total rows" value={(tables.data?.total_rows ?? 0).toLocaleString()} />
        <KPI label="Unreadable" value={unreadable} hint={unreadable ? 'counted as “Error”' : undefined} />
      </KPIRow>
      <Section title="Technology catalog — table inventory"
        right={<Btn small onClick={tables.reload}>Refresh</Btn>}>
        {tables.error && <Msg kind="error">{tables.error}</Msg>}
        {/* The handler reports a missing/locked catalog file in-band rather than 500ing. */}
        {tables.data?.error && <Msg kind="error">{tables.data.error}</Msg>}
        {tables.loading ? <Loading /> : (
          <DataTable rows={rows} csvName="catalog_tables" maxHeight="24rem"
            columns={['table', 'type', 'rows', 'columns']}
            labels={{ rows: 'Row count', columns: 'Columns' }}
            empty="Catalog reported no tables." />
        )}
      </Section>
      <SqlRunner action="sqlite_sql" title="Catalog SQL console — read-only"
        blurb="One SELECT/WITH statement against the technology catalog. The file is opened read-only at the OS level, the run is interrupted after 20s, oversized cells are truncated."
        initial="SELECT * FROM technologies LIMIT 10" csvName="catalog_query_result"
        limits={['100', '250', '500']}
        templates={templates.data || []} templatesError={templates.error} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// One console shared by both databases: same guarantees, different dispatcher
// action. Neither can write, so neither needs a confirm step.
// ---------------------------------------------------------------------------
const SqlRunner: React.FC<{
  action: 'sql' | 'sqlite_sql';
  title: string;
  blurb: string;
  initial: string;
  csvName: string;
  limits?: string[];
  templates?: { label: string; query: string }[];
  templatesError?: string;
}> = ({ action, title, blurb, initial, csvName, limits, templates, templatesError }) => {
  const [query, setQuery] = useState(initial);
  const [limit, setLimit] = useState(limits ? limits[limits.length - 1] : '');
  const [result, setResult] = useState<any>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (sql: string) => {
    setBusy(true); setErr(''); setResult(null);
    const body: Record<string, any> = { query: sql };
    if (limits) body.limit = Number(limit);
    const { data, error } = await panelPost('system', action, body);
    setBusy(false);
    if (error) setErr(error);
    else setResult(data);
  };

  // SELECT a, a would collapse into one property, so repeated names are suffixed.
  const cols: string[] = (() => {
    if (!result?.columns) return [];
    const seen: Record<string, number> = {};
    return result.columns.map((c: string) => {
      seen[c] = (seen[c] || 0) + 1;
      return seen[c] > 1 ? `${c} (${seen[c]})` : c;
    });
  })();
  const resultRows: any[] = result?.rows
    ? result.rows.map((r: any[]) => Object.fromEntries(cols.map((c, i) => [c, r[i]])))
    : [];
  // Postgres only: the handler blanks secret-looking columns (password hashes,
  // session tokens) to "<redacted>" and names them here, so `SELECT *` doesn't
  // look like a table full of empty strings.
  const redacted: string[] = result?.redacted_columns || [];

  return (
    <Section title={title}>
      <p className="text-xs text-grayscale-1100 -mt-1">{blurb}</p>
      {templatesError && <Msg kind="error">{templatesError}</Msg>}
      {!!templates?.length && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">Quick queries:</span>
          {templates.map(t => (
            <Btn key={t.label} small
              onClick={() => { setQuery(t.query); run(t.query); }}>{t.label}</Btn>
          ))}
        </div>
      )}
      <textarea value={query} rows={5} onChange={e => setQuery(e.target.value)}
        className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm font-mono"
        spellCheck={false} />
      <div className="flex items-center gap-2 flex-wrap">
        <Btn kind="primary" disabled={busy || !query.trim()} onClick={() => run(query)}>
          {busy ? 'Running…' : 'Run query'}
        </Btn>
        {limits && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-grayscale-1100">Row limit</span>
            <Select value={limit} onChange={setLimit} options={limits} />
          </div>
        )}
        {result?.truncated && (
          <span className="text-xs text-amber-700">
            Showing the first {result.limit ?? 500} rows — add a LIMIT or narrow the query.
          </span>
        )}
        {result && typeof result.elapsed_ms === 'number' && (
          <span className="text-xs text-grayscale-1100">
            {result.count ?? resultRows.length} row(s) in {result.elapsed_ms} ms
          </span>
        )}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      {!!redacted.length && (
        <p className="text-xs text-amber-700">
          Redacted {redacted.length === 1 ? 'column' : 'columns'}:{' '}
          <span className="font-mono">{redacted.join(', ')}</span> — the server replaces
          secret values with <span className="font-mono">&lt;redacted&gt;</span> before the
          result leaves it, so this console never carries a password hash or a live token
          into the browser. Every other column is the real value.
        </p>
      )}
      {result && (
        <DataTable rows={resultRows} columns={cols} csvName={csvName} searchable
          empty="Query ran and returned no rows." />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Jobs — the admin_jobs queue the AI labs (TRL, Horizon) run their batches on.
// ---------------------------------------------------------------------------
const Jobs: React.FC = () => {
  const [limit, setLimit] = useState('20');
  const [status, setStatus] = useState('All');
  const [auto, setAuto] = useState(true);
  const jobs = useAdminGet('labs', 'jobs', { limit }, [limit]);
  const all: any[] = jobs.data || [];
  const running = all.filter(j => j.status === 'running').length;
  const reload = jobs.reload;

  // A batch reports progress from its worker thread, so the table is only ever
  // as fresh as its last read — poll while something is actually in flight.
  useEffect(() => {
    if (!auto || !running) return;
    const t = setInterval(() => reload(), 5000);
    return () => clearInterval(t);
  }, [auto, running, reload]);

  const rows = all
    .filter(j => status === 'All' || j.status === status)
    .map(j => {
      const total = Number(j.total) || 0;
      const done = Number(j.done) || 0;
      return {
        ...j,
        progress: total ? `${done}/${total}` : `${done}`,
        _pct: total ? Math.min(100, Math.round((done / total) * 100)) : (j.status === 'done' ? 100 : 0),
      };
    });

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Jobs shown" value={all.length} />
        <KPI label="Running" value={running} hint={running && auto ? 'auto-refreshing' : undefined} />
        <KPI label="Done" value={all.filter(j => j.status === 'done').length} />
        <KPI label="Failed" value={all.filter(j => j.status === 'failed').length} />
      </KPIRow>
      <Section title="Background batch jobs"
        right={<div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-grayscale-1200">
            <input type="checkbox" checked={auto} onChange={e => setAuto(e.target.checked)} />
            Auto-refresh while running
          </label>
          <Btn small onClick={reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Every TRL and Horizon batch runs on a worker thread that survives the request that
          started it; this is what it reports back. Nothing here is startable or cancellable —
          runs begin in the AI Labs panel.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={status} onChange={setStatus} options={['All', 'running', 'done', 'failed']}
            labels={{ All: 'All statuses' }} />
          <Select value={limit} onChange={setLimit} options={['20', '50', '100', '200']}
            labels={{ '20': 'Last 20', '50': 'Last 50', '100': 'Last 100', '200': 'Last 200' }} />
        </div>
        {jobs.error && <Msg kind="error">{jobs.error}</Msg>}
        {jobs.loading ? <Loading /> : (
          <DataTable rows={rows} csvName="labs_jobs" maxHeight="34rem" searchable
            columns={['created_at', 'kind', 'status', 'progress', 'note', 'admin_email', 'error', 'ref_id', 'id']}
            labels={{ created_at: 'Started', admin_email: 'Admin', ref_id: 'Ref', id: 'Job ID' }}
            empty="No batch jobs have been run yet."
            render={{
              status: r => <StatusPill value={r.status} />,
              progress: r => (
                <div className="flex items-center gap-2 min-w-[9rem]">
                  <div className="flex-1 bg-grayscale-200 rounded-full h-2 overflow-hidden">
                    <div className="h-2 bg-primary-500 rounded-full" style={{ width: `${r._pct}%` }} />
                  </div>
                  <span className="text-xs text-grayscale-1100">{r.progress}</span>
                </div>
              ),
              error: r => r.error
                ? <span className="text-red-600" title={r.error}>{r.error}</span>
                : <span className="text-grayscale-1100">—</span>,
            }} />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Integrations — the read-only replacement for the Streamlit sidebar's API-key
// form. Booleans only; no handler in this group returns a key value.
// ---------------------------------------------------------------------------
const Flag: React.FC<{ label: string; on: boolean; hint: string }> = ({ label, on, hint }) => (
  <div className="flex items-start gap-3 py-2 border-t border-grayscale-200 first:border-t-0">
    <span className={`mt-0.5 text-xs rounded-full px-2 py-0.5 border whitespace-nowrap ${on
      ? 'text-green-700 bg-green-50 border-green-200'
      : 'text-grayscale-1100 bg-grayscale-200 border-grayscale-300'}`}>
      {on ? 'Configured' : 'Not set'}
    </span>
    <div className="flex flex-col">
      <span className="text-sm font-medium text-grayscale-1300">{label}</span>
      <span className="text-xs text-grayscale-1100">{hint}</span>
    </div>
  </div>
);

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-grayscale-1100">{label}</span>
    <span className="text-grayscale-1300 font-medium text-right break-all">{value}</span>
  </div>
);

const Integrations: React.FC = () => {
  const cfg = useAdminGet('users', 'task_config');
  const mail = useAdminGet('email', 'send_config');
  const c = cfg.data || {};
  const m = mail.data || {};

  return (
    <div className="flex flex-col gap-4">
      <Section title="Server-side integrations"
        right={<Btn small onClick={() => { cfg.reload(); mail.reload(); }}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Keys live in <span className="font-mono">Backend/.env</span> and are read by the server
          only — this page reports whether each one is set and never shows or edits its value
          (the legacy Streamlit sidebar put live API keys in text boxes; this replaces it).
        </p>
        {cfg.error && <Msg kind="error">{cfg.error}</Msg>}
        {cfg.loading ? <Loading /> : (
          <div className="flex flex-col">
            <Flag label="BrightData" on={!!c.brightdata_configured}
              hint="Scrapes the LinkedIn profile behind a user-creation task." />
            <Flag label="Azure OpenAI" on={!!c.azure_configured}
              hint="Summarises the scraped profile; needs both the key and the endpoint." />
            <Flag label="LinkedIn enrichment" on={!!c.linkedin_enabled}
              hint="On only when BrightData and Azure are both configured. Off means approvals still create the account, with an empty professional summary." />
            <Flag label="SendGrid" on={!!c.sendgrid_configured}
              hint="Every outbound email — invitations, approvals, campaigns, OTP. Off means sends are recorded as failures." />
            <Flag label="OpenAI (Horizon runs)" on={!!c.openai_configured}
              hint={`OPENAI_API_KEY — runs Horizon on the pending technologies from Horizon data → Pending (Azure OpenAI above is the other provider). Off means those runs are refused${c.openai_configured ? '' : ' — add OPENAI_API_KEY to Backend/.env on the API server and restart the API'}.`} />
          </div>
        )}
      </Section>
      <div className="grid gap-4 md:grid-cols-2">
        <Section title="User-creation queue">
          {cfg.loading ? <Loading /> : (
            <div className="flex flex-col divide-y divide-grayscale-200">
              <Fact label="Worker slots" value={c.worker_slots ?? '—'} />
              <Fact label="Max LinkedIn attempts" value={c.max_linkedin_attempts ?? '—'} />
              <Fact label="Task statuses" value={(c.statuses || []).join(', ') || '—'} />
              <Fact label="Task types" value={(c.task_types || []).join(', ') || '—'} />
            </div>
          )}
        </Section>
        <Section title="SendGrid sender defaults">
          {mail.error && <Msg kind="error">{mail.error}</Msg>}
          {mail.loading ? <Loading /> : (
            <div className="flex flex-col divide-y divide-grayscale-200">
              <Fact label="Can send" value={m.sendgrid_configured ? 'Yes' : 'No — key not set'} />
              <Fact label="From email" value={m.default_from_email || '—'} />
              <Fact label="From name" value={m.default_from_name || '—'} />
              <Fact label="Default link" value={m.default_bitly || '—'} />
              <Fact label="Max recipients / campaign" value={(m.max_recipients ?? '—').toLocaleString?.() ?? m.max_recipients} />
              <Fact label="Max rows / bulk upload" value={(m.max_bulk_rows ?? '—').toLocaleString?.() ?? m.max_bulk_rows} />
              <Fact label="Attachment types" value={(m.attachment_extensions || []).join(' ') || '—'} />
              <Fact label="Placeholders" value={(m.placeholders || []).join(' ') || '—'} />
            </div>
          )}
        </Section>
      </div>
    </div>
  );
};

export default SystemPanel;
